import axios from '@/utils/axios';
import { API_KEY } from '@/config';
import {
  GetScheduledJobRunsParams,
  GetScheduledJobsParams,
  ScheduledJob,
  ScheduledJobPayload,
  ScheduledJobRun,
  ScheduledJobRunsResponse,
  ScheduledJobsListResponse,
  ScheduledJobsPaginationMeta,
} from '../types/scheduled-jobs';

type UnknownRecord = Record<string, unknown>;

const API_KEY_HEADER = 'x-api-key';

const toRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};

const firstDefined = <T>(...values: T[]) =>
  values.find((value) => value !== undefined && value !== null);

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const toString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const toBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

const normalizeMeta = (value: unknown): ScheduledJobsPaginationMeta | null => {
  const meta = toRecord(value);
  if (!Object.keys(meta).length) return null;

  return {
    total:
      toNumber(meta.total) ??
      toNumber(meta.total_count) ??
      toNumber(meta.totalCount) ??
      toNumber(meta.count) ??
      null,
    totalPages:
      toNumber(meta.totalPages) ??
      toNumber(meta.total_pages) ??
      toNumber(meta.last_page) ??
      toNumber(meta.pages) ??
      null,
    page:
      toNumber(meta.page) ??
      toNumber(meta.current_page) ??
      toNumber(meta.pageNumber) ??
      null,
    limit:
      toNumber(meta.limit) ??
      toNumber(meta.per_page) ??
      toNumber(meta.pageSize) ??
      null,
  };
};

const extractCollection = (
  payload: unknown,
  collectionKeys: string[]
): unknown[] => {
  const root = toRecord(payload);
  const data = toRecord(root.data);
  const nestedData = toRecord(data.data);

  const candidates = [
    root.data,
    data.data,
    nestedData.data,
    ...collectionKeys.map((key) => nestedData[key]),
    ...collectionKeys.map((key) => data[key]),
    ...collectionKeys.map((key) => root[key]),
    root.results,
    root.items,
  ];

  return (candidates.find(Array.isArray) as unknown[] | undefined) ?? [];
};

const extractMeta = (payload: unknown): ScheduledJobsPaginationMeta | null => {
  const root = toRecord(payload);
  const data = toRecord(root.data);
  const nestedData = toRecord(data.data);

  return (
    normalizeMeta(root.meta) ??
    normalizeMeta(root.pagination) ??
    normalizeMeta(data.meta) ??
    normalizeMeta(data.pagination) ??
    normalizeMeta(nestedData.meta) ??
    normalizeMeta(nestedData.pagination) ??
    normalizeMeta(nestedData) ??
    normalizeMeta(data) ??
    normalizeMeta(root)
  );
};

const normalizeExecutionType = (value: unknown): ScheduledJob['executionType'] =>
  value === 'INTERNAL' ? 'INTERNAL' : 'HTTP';

const normalizeJob = (value: unknown, fallbackIndex: number = 0): ScheduledJob => {
  const item = toRecord(value);

  return {
    id:
      (firstDefined(item.id, item.jobId, item.job_id) as number | string) ??
      `scheduled-job-${fallbackIndex}`,
    name: toString(item.name) ?? '',
    description: toString(item.description) ?? '',
    enabled: toBoolean(firstDefined(item.enabled, item.active)),
    cronExpression:
      toString(firstDefined(item.cronExpression, item.cron_expression)) ?? '',
    timeZone: toString(firstDefined(item.timeZone, item.time_zone)) ?? '',
    executionType: normalizeExecutionType(
      firstDefined(item.executionType, item.execution_type)
    ),
    httpMethod: toString(firstDefined(item.httpMethod, item.http_method)),
    targetUrl: toString(firstDefined(item.targetUrl, item.target_url)),
    headersJson: firstDefined(item.headersJson, item.headers_json),
    bodyJson: firstDefined(item.bodyJson, item.body_json),
    handlerKey: toString(firstDefined(item.handlerKey, item.handler_key)),
    timeoutMs: toNumber(firstDefined(item.timeoutMs, item.timeout_ms)),
    lockKey: toString(firstDefined(item.lockKey, item.lock_key)),
    lastStatus: toString(firstDefined(item.lastStatus, item.last_status)),
    lastRunAt: toString(firstDefined(item.lastRunAt, item.last_run_at)),
    lastError: toString(firstDefined(item.lastError, item.last_error)),
    createdAt: toString(firstDefined(item.createdAt, item.created_at)),
    updatedAt: toString(firstDefined(item.updatedAt, item.updated_at)),
  };
};

const normalizeRun = (value: unknown, fallbackIndex: number = 0): ScheduledJobRun => {
  const item = toRecord(value);

  return {
    id:
      (firstDefined(item.id, item.runId, item.run_id) as number | string) ??
      `scheduled-job-run-${fallbackIndex}`,
    status: toString(item.status),
    trigger: toString(item.trigger),
    triggeredFor: toString(firstDefined(item.triggeredFor, item.triggered_for)),
    startedAt: toString(firstDefined(item.startedAt, item.started_at)),
    finishedAt: toString(firstDefined(item.finishedAt, item.finished_at)),
    durationMs: toNumber(firstDefined(item.durationMs, item.duration_ms)),
    error: toString(item.error),
    responseStatus:
      (firstDefined(
        item.responseStatus,
        item.response_status
      ) as number | string | null) ?? null,
    responseBody: firstDefined(item.responseBody, item.response_body),
    responseBodyPreview: toString(
      firstDefined(item.responseBodyPreview, item.response_body_preview)
    ),
    createdAt: toString(firstDefined(item.createdAt, item.created_at)),
  };
};

const EXAMPLE_JOB_NAMES = new Set([
  'example external http heartbeat',
  'example internal health check',
]);

const EXAMPLE_JOB_LOCK_KEYS = new Set([
  'scheduled-job:example-http-heartbeat',
  'scheduled-job:example-internal-health-check',
]);

const isExampleScheduledJob = (job: ScheduledJob) =>
  EXAMPLE_JOB_NAMES.has(job.name.toLowerCase()) ||
  EXAMPLE_JOB_LOCK_KEYS.has((job.lockKey ?? '').toLowerCase());

const withApiKeyHeaderConfig = () =>
  API_KEY
    ? {
        headers: {
          [API_KEY_HEADER]: API_KEY,
        },
      }
    : undefined;

const getApiKeyHeaderValue = (headersJson: unknown) => {
  const header = Object.entries(toRecord(headersJson)).find(
    ([key]) => key.toLowerCase() === API_KEY_HEADER
  );

  if (!header) return null;
  return typeof header[1] === 'string' ? header[1] : String(header[1]);
};

const shouldPersistExecutionApiKeyHeader = (job: ScheduledJob) =>
  Boolean(
    API_KEY &&
      job.executionType === 'HTTP' &&
      getApiKeyHeaderValue(job.headersJson) !== API_KEY
  );

const withExecutionApiKeyHeader = <T extends Partial<ScheduledJobPayload>>(
  payload: T
): T => {
  if (!API_KEY || payload.executionType !== 'HTTP') return payload;

  const headers = Object.entries(toRecord(payload.headersJson)).reduce(
    (nextHeaders, [key, value]) => {
      if (key.toLowerCase() !== API_KEY_HEADER) {
        nextHeaders[key] = value;
      }

      return nextHeaders;
    },
    {} as UnknownRecord
  );

  return {
    ...payload,
    headersJson: {
      ...headers,
      [API_KEY_HEADER]: API_KEY,
    },
  };
};

const extractEntity = (payload: unknown): unknown => {
  const root = toRecord(payload);
  const data = toRecord(root.data);
  const entity =
    data.data ??
    data.job ??
    data.scheduledJob ??
    data.scheduled_job ??
    root.data ??
    root;
  const entityRecord = toRecord(entity);

  return (
    entityRecord.job ??
    entityRecord.scheduledJob ??
    entityRecord.scheduled_job ??
    entity
  );
};

export async function getScheduledJobs(
  params?: GetScheduledJobsParams
): Promise<ScheduledJobsListResponse> {
  const response = await axios.get('/admin/scheduled-jobs', { params });
  const payload = response?.data ?? {};
  const allJobs = extractCollection(payload, [
    'jobs',
    'scheduledJobs',
    'scheduled_jobs',
  ]).map(normalizeJob);
  const jobs = allJobs.filter((job) => !isExampleScheduledJob(job));
  const meta = extractMeta(payload);

  return {
    data: jobs,
    meta: meta?.total
      ? { ...meta, total: Math.max(0, meta.total - (allJobs.length - jobs.length)) }
      : meta,
  };
}

export async function createScheduledJob(
  payload: ScheduledJobPayload
): Promise<ScheduledJob> {
  const response = await axios.post(
    '/admin/scheduled-jobs',
    withExecutionApiKeyHeader(payload)
  );
  return normalizeJob(extractEntity(response?.data));
}

export async function updateScheduledJob(
  id: ScheduledJob['id'],
  payload: Partial<ScheduledJobPayload>
): Promise<ScheduledJob> {
  const response = await axios.patch(
    `/admin/scheduled-jobs/${id}`,
    withExecutionApiKeyHeader(payload)
  );
  return normalizeJob(extractEntity(response?.data));
}

export async function deleteScheduledJob(
  id: ScheduledJob['id']
): Promise<unknown> {
  const response = await axios.delete(`/admin/scheduled-jobs/${id}`);
  return response?.data;
}

export async function runScheduledJob(id: ScheduledJob['id']): Promise<unknown> {
  const response = await axios.post(
    `/admin/scheduled-jobs/${id}/run`,
    undefined,
    withApiKeyHeaderConfig()
  );
  return response?.data;
}

export async function runScheduledJobWithLatestHeaders(
  job: ScheduledJob
): Promise<unknown> {
  if (shouldPersistExecutionApiKeyHeader(job)) {
    await updateScheduledJob(job.id, {
      executionType: job.executionType,
      headersJson: job.headersJson,
    });
  }

  const response = await axios.post(
    `/admin/scheduled-jobs/${job.id}/run`,
    undefined,
    withApiKeyHeaderConfig()
  );
  return response?.data;
}

export async function getScheduledJobRuns(
  id: ScheduledJob['id'],
  params?: GetScheduledJobRunsParams
): Promise<ScheduledJobRunsResponse> {
  const response = await axios.get(`/admin/scheduled-jobs/${id}/runs`, {
    params,
  });
  const payload = response?.data ?? {};

  return {
    data: extractCollection(payload, ['runs', 'jobRuns', 'job_runs']).map(
      normalizeRun
    ),
    meta: extractMeta(payload),
  };
}
